/**
 * Shared validation utilities to eliminate duplication across validation routes
 */

// Import base types to eliminate duplication
import type { Address, BillingAddress, ValidationResult } from '@/lib/types/base';

// Re-export types
export type { Address, BillingAddress, ValidationResult };

/**
 * Email validation utility
 */
export function validateEmail(email: string): { isValid: boolean; error?: string } {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Email must be a valid email address' };
  }

  if (email.length > 255) {
    return { isValid: false, error: 'Email must be 255 characters or less' };
  }

  return { isValid: true };
}

/**
 * Phone validation utility
 */
export function validatePhone(phone: string): { isValid: boolean; warning?: string } {
  if (!phone) return { isValid: true };

  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  if (!phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''))) {
    return { isValid: true, warning: 'Phone number format may be invalid' };
  }

  return { isValid: true };
}

/**
 * Address validation utility
 */
export function validateAddress(address: Address, prefix: string = ''): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (address.addressLine1 && address.addressLine1.length > 100) {
    errors.push(`${prefix}Address line 1 must be 100 characters or less`);
  }
  if (address.addressLine2 && address.addressLine2.length > 100) {
    errors.push(`${prefix}Address line 2 must be 100 characters or less`);
  }
  if (address.city && address.city.length > 50) {
    errors.push(`${prefix}City must be 50 characters or less`);
  }
  if (address.postalCode && address.postalCode.length > 20) {
    errors.push(`${prefix}Postal code must be 20 characters or less`);
  }
  if (address.stateOrProvince && address.stateOrProvince.length > 50) {
    errors.push(`${prefix}State/Province must be 50 characters or less`);
  }
  if (address.country && address.country.length > 50) {
    errors.push(`${prefix}Country must be 50 characters or less`);
  }

  return { errors, warnings };
}

/**
 * Billing address validation utility
 */
export function validateBillingAddress(billingAddress: BillingAddress): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (billingAddress.company && billingAddress.company.length > 100) {
    errors.push('Billing company must be 100 characters or less');
  }

  const addressValidation = validateAddress(billingAddress, 'Billing ');
  errors.push(...addressValidation.errors);
  warnings.push(...addressValidation.warnings);

  return { errors, warnings };
}

/**
 * Website URL validation utility
 */
export function validateWebsiteUrl(website: string): { isValid: boolean; warning?: string } {
  if (!website) return { isValid: true };

  try {
    // Add protocol if missing
    const url = website.startsWith('http') ? website : `https://${website}`;
    new URL(url);
    return { isValid: true };
  } catch {
    return { isValid: true, warning: 'Website URL format may be invalid' };
  }
}

/**
 * Name validation utility
 */
export function validateName(name: string, fieldName: string, maxLength: number = 50): { errors: string[] } {
  const errors: string[] = [];

  if (!name || name.trim() === '') {
    errors.push(`${fieldName} is required`);
  }

  if (name && name.length > maxLength) {
    errors.push(`${fieldName} must be ${maxLength} characters or less`);
  }

  return { errors };
}

/**
 * Description validation utility
 */
export function validateDescription(description: string, maxLength: number = 500): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (description && description.length > maxLength) {
    errors.push(`Description must be ${maxLength} characters or less`);
  }

  return { errors, warnings };
}

/**
 * Tags validation utility
 */
export function validateTags(tags: unknown): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!tags) return { errors, warnings };

  if (Array.isArray(tags)) {
    if (tags.length > 20) {
      warnings.push('Large number of tags may affect performance');
    }
    tags.forEach((tag: unknown, index: number) => {
      if (typeof tag !== 'string') {
        errors.push(`Tag at index ${index} must be a string`);
      } else if (tag.length > 50) {
        errors.push(`Tag "${tag}" must be 50 characters or less`);
      }
    });
  } else if (typeof tags === 'string') {
    // Handle comma-separated string format
    const tagArray = tags.split(',').map((tag: string) => tag.trim());
    if (tagArray.length > 20) {
      warnings.push('Large number of tags may affect performance');
    }
  } else {
    errors.push('Tags must be an array or string');
  }

  return { errors, warnings };
}

/**
 * Status validation utility
 */
export function validateStatus<T extends string>(status: T, validStatuses: T[], entityType: string): { errors: string[] } {
  const errors: string[] = [];

  if (status && !validStatuses.includes(status)) {
    errors.push(`${entityType} status must be one of: ${validStatuses.join(', ')}`);
  }

  return { errors };
}

/**
 * Create standardized validation response
 */
export function createValidationResponse(errors: string[], warnings: string[]): ValidationResult {
  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Check for duplicate entities by name
 */
export async function checkDuplicateName<T extends { name: string }>(
  name: string,
  existingEntities: T[],
  entityType: string,
  caseSensitive: boolean = false
): Promise<{ errors: string[]; warnings: string[] }> {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (!name) return { errors, warnings };

  try {
    const nameToCheck = caseSensitive ? name : name.toLowerCase();
    const duplicate = existingEntities.some(entity => {
      const entityName = caseSensitive ? entity.name : entity.name.toLowerCase();
      return entityName === nameToCheck;
    });

    if (duplicate) {
      errors.push(`A ${entityType} with this name already exists`);
    }
  } catch {
    warnings.push(`Could not verify ${entityType} name uniqueness`);
  }

  return { errors, warnings };
}

/**
 * Validate year range
 */
export function validateYear(year: number | undefined, fieldName: string, minYear: number = 1800): { errors: string[] } {
  const errors: string[] = [];

  if (year !== undefined) {
    const currentYear = new Date().getFullYear();
    if (typeof year !== 'number' || year < minYear || year > currentYear) {
      errors.push(`${fieldName} must be between ${minYear} and ${currentYear}`);
    }
  }

  return { errors };
}

/**
 * Validate positive number
 */
export function validatePositiveNumber(value: number | undefined, fieldName: string, maxValue?: number): { errors: string[]; warnings: string[] } {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (value !== undefined) {
    if (typeof value !== 'number' || value < 0) {
      errors.push(`${fieldName} must be a non-negative number`);
    } else if (maxValue && value > maxValue) {
      warnings.push(`${fieldName} is unusually high (over ${maxValue})`);
    }
  }

  return { errors, warnings };
}

/**
 * Validate time format (HH:MM)
 */
export function validateTimeFormat(time: string, fieldName: string): { errors: string[] } {
  const errors: string[] = [];

  if (time) {
    const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(time)) {
      errors.push(`${fieldName} must be in HH:MM format`);
    }
  }

  return { errors };
}

/**
 * Validate time range
 */
export function validateTimeRange(startTime: string, endTime: string): { errors: string[] } {
  const errors: string[] = [];

  if (startTime && endTime) {
    if (startTime >= endTime) {
      errors.push('Start time must be before end time');
    }
  }

  return { errors };
}

/**
 * Validate date format (YYYY-MM-DD)
 */
export function validateDateFormat(date: string, fieldName: string): { errors: string[] } {
  const errors: string[] = [];

  if (date) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(date)) {
      errors.push(`${fieldName} must be in YYYY-MM-DD format`);
    } else {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        errors.push(`${fieldName} must be a valid date`);
      }
    }
  }

  return { errors };
} 
